/**
 * swms_live_controller.js — live refresh for the SWMS detail page.
 *
 * The SWMS sign / require / finalise flows are plain form POSTs that redirect,
 * so the device that acted re-renders itself. OTHER devices viewing the same
 * SWMS need a nudge to repaint. realtime-hub.js owns the single EventSource and
 * re-dispatches each `swms_changed` signal as the `chippie:swms` document event;
 * this page-scoped controller listens for it and, when it names THIS swms,
 * re-checks the page and shows the server-supplied message.
 *
 * WHY ITS OWN EVENT TYPE (swms_changed), not the generic entity_changed: the
 * shared sse-entity-updates consumer announces a toast for EVERY entity_changed
 * it receives and refetches a component via GET /api/turbo/refresh/<entity>/<id>
 * — a CLOSED registry covering only quote/job/invoice/client. SWMS has no
 * refresh component, so routing it through entity_changed would give a toast but
 * no repaint, AND double up with this controller's own toast. A dedicated event
 * type the generic consumer never listens for keeps that consumer untouched and
 * leaves this controller the single owner of the SWMS detail page's live update.
 *
 * ORIGINATOR-SKIP: the device that wrote already redirected to fresh state, so
 * we drop the echo of our own tab. tab-session.js stamps X-Tab-Id on the form
 * POST (Turbo submits it through window.fetch); the server reflects it back as
 * `originator_tab_id`, which we compare against window.chippieSession.tabId.
 *
 * Config: data-swms-live-id is this record's id (the signal's `id` must match).
 * Depends on: window.chippiePageCheck, realtime-hub.js (the `chippie:swms`
 * event), tab-session.js (window.chippieSession.tabId).
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            this.swmsId = String(this.element.dataset.swmsLiveId || '');
            this._onEvent = this._handle.bind(this);
            document.addEventListener('chippie:swms', this._onEvent);
            // A live event below reloads the page, which wipes the toast the
            // shared consumer just showed. We stash the message and re-show it
            // here on the fresh load (same sessionStorage handoff notifications.js
            // uses for messages that must survive a navigation).
            this._flushMessage();
        }

        disconnect() {
            document.removeEventListener('chippie:swms', this._onEvent);
        }

        _handle(event) {
            var data = event.detail;
            if (!data || String(data.id) !== this.swmsId) return;
            // Our own write already showed fresh state via its redirect.
            if (data.originator_tab_id
                && window.chippieSession
                && data.originator_tab_id === window.chippieSession.tabId) {
                return;
            }
            // The reload below replaces <body> and wipes any toast shown now, so
            // hand the server's message to the fresh load via sessionStorage
            // (flushed in connect()). Private-mode failures just skip the toast.
            if (data.message) {
                try {
                    sessionStorage.setItem('swms_live_message', data.message);
                } catch (_e) { /* no storage: refresh without the toast */ }
            }

            // The page check, not a Turbo visit: the saved copy predates the change.
            window.chippiePageCheck.refresh();
        }

        // Show (once) any message a live event stashed before reloading the page.
        _flushMessage() {
            var msg;
            try {
                msg = sessionStorage.getItem('swms_live_message');
                if (msg) sessionStorage.removeItem('swms_live_message');
            } catch (_e) {
                return;
            }
            if (msg && typeof window.showNotification === 'function') {
                window.showNotification(msg, 'info');
            }
        }
}
